import React, { useState } from "react";
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, AppBar, Typography, IconButton, Divider, Avatar, Menu,
  MenuItem, useMediaQuery, useTheme,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ArticleIcon from "@mui/icons-material/Article";
import TranslateIcon from "@mui/icons-material/Translate";
import PeopleIcon from "@mui/icons-material/People";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./context/AuthContext";

const DRAWER_WIDTH = 240;

const NAV_ITEMS = [
  { label: "Dashboard", icon: <DashboardIcon />, path: "/admin" },
  { label: "Fleet", icon: <DirectionsCarIcon />, path: "/admin/fleet" },
  { label: "Blog", icon: <ArticleIcon />, path: "/admin/blog" },
  { label: "Translations", icon: <TranslateIcon />, path: "/admin/translations" },
  { label: "Users", icon: <PeopleIcon />, path: "/admin/users" },
];

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const drawer = (
    <Box sx={{ backgroundColor: "#1C2541", height: "100%", color: "white" }}>
      <Toolbar sx={{ justifyContent: "center" }}>
        <Typography variant="h6" fontWeight="bold" color="#EE8013">
          Why Not
        </Typography>
        <Typography variant="h6" fontWeight="bold" color="white" ml={0.5}>
          Admin
        </Typography>
      </Toolbar>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
      <List>
        {NAV_ITEMS.map((item) => {
          const active = location.pathname === item.path;
          return (
            <ListItemButton
              key={item.path}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              sx={{
                mx: 1, borderRadius: 2, mb: 0.5,
                backgroundColor: active ? "rgba(238,128,19,0.15)" : "transparent",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
              }}
            >
              <ListItemIcon sx={{ color: active ? "#EE8013" : "rgba(255,255,255,0.7)", minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} sx={{ color: active ? "white" : "rgba(255,255,255,0.7)" }} />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1, backgroundColor: "white", color: "#1C2541", boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>
        <Toolbar sx={{ justifyContent: "space-between" }}>
          {isMobile && (
            <IconButton onClick={() => setMobileOpen(true)} sx={{ color: "#1C2541" }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="subtitle1" fontWeight="bold" color="#1C2541">
            {NAV_ITEMS.find((i) => i.path === location.pathname)?.label ?? "Admin"}
          </Typography>
          <Box>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <Avatar sx={{ width: 32, height: 32, backgroundColor: "#1C2541", fontSize: 14 }}>
                {user?.name?.[0]?.toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem disabled>
                <Typography variant="body2">{user?.name} ({user?.role})</Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { logout(); navigate("/admin/login"); }}>
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Sign Out
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {isMobile ? (
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ "& .MuiDrawer-paper": { width: DRAWER_WIDTH } }}>
          {drawer}
        </Drawer>
      ) : (
        <Drawer variant="permanent"
          sx={{ width: DRAWER_WIDTH, flexShrink: 0, "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" } }}>
          {drawer}
        </Drawer>
      )}

      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8, backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
        {children}
      </Box>
    </Box>
  );
};

export default AdminLayout;
